import { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { useFrame, useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import {
  useGameStore,
  IsometricCamera,
  WorldLighting,
  IsometricFloor,
  WeatherSystem,
  COLORS,
  LIGHTING,
  MONUMENT_PALETTES,
  PALETTE_LIGHTING,
} from '@hollowveil/shared';
import type { PaletteName } from '@hollowveil/shared';
import { RoomRenderer } from '../components/game/RoomRenderer';
import { Player } from '../components/game/Player';
import { useSounds } from '../hooks/useSounds';

// Thunder sound files
const THUNDER_SOUNDS = [
  'effect_thunder_test_1',
  'effect_thunder_test2',
];

// Neon blink sound files
const NEON_SOUNDS = [
  'effect_neon_test1',
  'effect_neon_test2',
];

// Rain drop sound files
const RAIN_DROP_SOUNDS = [
  'effect_goutte0',
  'effect_goutte1',
  'effect_goutte2',
  'effect_goutte3',
];

// Ambient particle interface
interface AmbientParticle {
  id: number;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  scale: number;
  opacity: number;
  phase: number;
}

export function GameScene() {
  const { currentWorld, currentRoom, isTransitioning, transitionProgress, setTargetPosition, inputMode, cameraZoom, player, visualEffect, setVisualEffect } =
    useGameStore();
  const { scene } = useThree();
  const { playEffect, playEffectWithFx, playAmbient, stopAmbient } = useSounds();

  // Play ambient music based on current room
  useEffect(() => {
    if (!currentRoom) return;

    // Hawkins level - play Stranger Things theme
    if (currentRoom.id === 'room_hawkins_reveil') {
      playAmbient('StrangerThingsTheme', 2000); // Fade in over 2 seconds
    } else {
      // Stop ambient music when leaving Hawkins
      stopAmbient(1500); // Fade out over 1.5 seconds
    }

    // Cleanup on unmount
    return () => {
      stopAmbient(500);
    };
  }, [currentRoom?.id, playAmbient, stopAmbient]);

  // Play random thunder sound when lightning strikes
  const handleLightningStrike = useCallback(() => {
    const randomSound = THUNDER_SOUNDS[Math.floor(Math.random() * THUNDER_SOUNDS.length)];
    // Add a small delay to simulate distance (light travels faster than sound)
    setTimeout(() => {
      playEffect(randomSound, 0.7 + Math.random() * 0.3);
    }, 100 + Math.random() * 300);
  }, [playEffect]);

  // Play random neon blink sound with distortion effect
  const handleNeonBlink = useCallback(() => {
    const randomSound = NEON_SOUNDS[Math.floor(Math.random() * NEON_SOUNDS.length)];
    // Apply distortion + lowpass for that electric buzzing neon sound
    playEffectWithFx(
      randomSound,
      [
        { type: 'distortion', amount: 0.3 + Math.random() * 0.3 },
        { type: 'lowpass', amount: 0.4 + Math.random() * 0.2 },
      ],
      0.15 + Math.random() * 0.1
    );
  }, [playEffectWithFx]);

  // Play random rain drop sound with reverb effect
  // Beaucoup de gouttes, très douces, beaucoup de reverb
  const handleRainDrop = useCallback(() => {
    const randomSound = RAIN_DROP_SOUNDS[Math.floor(Math.random() * RAIN_DROP_SOUNDS.length)];
    // Apply heavy reverb for ambient rain sound - very quiet
    playEffectWithFx(
      randomSound,
      [
        { type: 'reverb', amount: 0.7 + Math.random() * 0.25 }, // Beaucoup plus de reverb (0.7-0.95)
        { type: 'lowpass', amount: 0.4 + Math.random() * 0.3 }, // Filtre pour son plus doux
      ],
      0.03 + Math.random() * 0.05 // Volume très faible (0.03-0.08)
    );
  }, [playEffectWithFx]);

  // Track if pointer is held down for continuous movement
  const isPointerDown = useRef(false);
  // Track if this is a pinch gesture (2 fingers) - should not move player
  const isPinching = useRef(false);

  // Handle pointer down - start continuous tracking
  const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    // Don't start if clicking on UI or interactive objects
    e.stopPropagation();

    // Check for multi-touch (pinch gesture) - don't move player
    const nativeEvent = e.nativeEvent as PointerEvent;
    if (nativeEvent.pointerType === 'touch') {
      // Check if there are multiple touches active
      const touchCount = (window as unknown as { __activeTouches?: number }).__activeTouches || 0;
      if (touchCount >= 2) {
        isPinching.current = true;
        return;
      }
    }

    isPointerDown.current = true;
    const point = e.point;
    setTargetPosition([point.x, 0, point.z]);
  }, [setTargetPosition]);

  // Handle pointer move - update target while held
  const handlePointerMove = useCallback((e: ThreeEvent<PointerEvent>) => {
    if (!isPointerDown.current || isPinching.current) return;
    const point = e.point;
    setTargetPosition([point.x, 0, point.z]);
  }, [setTargetPosition]);

  // Handle pointer up - stop tracking
  const handlePointerUp = useCallback(() => {
    isPointerDown.current = false;
    isPinching.current = false;
  }, []);

  // Global pointer up listener to handle release outside the mesh
  useEffect(() => {
    const handleGlobalPointerUp = () => {
      isPointerDown.current = false;
    };
    window.addEventListener('pointerup', handleGlobalPointerUp);
    window.addEventListener('touchend', handleGlobalPointerUp);
    return () => {
      window.removeEventListener('pointerup', handleGlobalPointerUp);
      window.removeEventListener('touchend', handleGlobalPointerUp);
    };
  }, []);

  // Handle floor click for tap-to-move (single tap fallback)
  const handleFloorClick = useCallback((e: ThreeEvent<MouseEvent>) => {
    // Check if we clicked on an interactive object (they will call stopPropagation)
    // If this handler runs, it means we clicked on empty floor
    const point = e.point;
    // Only on touch mode or if it's a tap (not drag)
    if (inputMode === 'touch' || e.type === 'click') {
      setTargetPosition([point.x, 0, point.z]);
    }
  }, [setTargetPosition, inputMode]);

  // Update background and fog based on world and room palette
  useFrame(() => {
    // Get room-specific palette or default
    const roomPalette = currentRoom?.palette || 'sunset';
    const realPalette = MONUMENT_PALETTES[roomPalette as keyof typeof MONUMENT_PALETTES] || MONUMENT_PALETTES.sunset;
    const spectralColors = COLORS.spectral;

    // Get room-specific lighting or default
    const realLighting = PALETTE_LIGHTING[roomPalette as keyof typeof PALETTE_LIGHTING] || LIGHTING.real;
    const spectralLighting = LIGHTING.spectral;

    const t = isTransitioning ? transitionProgress : currentWorld === 'real' ? 0 : 1;

    // Interpolate background color (using sky color from palette)
    const bgColor = new THREE.Color(realPalette.sky).lerp(
      new THREE.Color(spectralColors.sky),
      t
    );
    scene.background = bgColor;

    // Interpolate fog for depth
    const fogColor = new THREE.Color(realLighting.fog.color).lerp(
      new THREE.Color(spectralLighting.fog.color),
      t
    );
    const fogNear =
      realLighting.fog.near + (spectralLighting.fog.near - realLighting.fog.near) * t;
    const fogFar =
      realLighting.fog.far + (spectralLighting.fog.far - realLighting.fog.far) * t;

    scene.fog = new THREE.Fog(fogColor, fogNear, fogFar);
  });

  if (!currentRoom) {
    return null;
  }

  const roomSize = currentRoom.size;

  return (
    <>
      {/* Camera - follows player position */}
      <IsometricCamera
        zoom={cameraZoom}
        target={[player.position[0], player.position[1], player.position[2]]}
        followSpeed={8}
      />

      {/* Lighting */}
      <WorldLighting
        world={currentWorld}
        transitionProgress={
          isTransitioning ? transitionProgress : currentWorld === 'real' ? 0 : 1
        }
      />

      {/* Floor */}
      <IsometricFloor
        width={roomSize[0]}
        depth={roomSize[2]}
        world={currentWorld}
        floorStyle={currentRoom.floorStyle || 'stone'}
      />

      {/* Invisible clickable floor for tap-to-move and drag-to-move */}
      {/* Much larger than room to capture all clicks/touches on screen */}
      <mesh
        position={[roomSize[0] / 2, -0.01, roomSize[2] / 2]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={handleFloorClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <planeGeometry args={[200, 200]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {/* Room content */}
      <RoomRenderer room={currentRoom} world={currentWorld} />

      {/* Spectral world lighting - chandeliers and ambient lights */}
      {currentWorld === 'spectral' && (
        <SpectralLighting roomSize={roomSize} />
      )}

      {/* Weather system */}
      {currentRoom.biome && currentRoom.weather !== 'none' && (
        <WeatherSystem
          weatherType={currentRoom.weather || 'none'}
          intensity={currentRoom.weatherIntensity}
          areaSize={[roomSize[0], roomSize[2]]}
          biome={currentRoom.biome}
          world={currentWorld}
          onLightningStrike={handleLightningStrike}
          onNeonBlink={handleNeonBlink}
          onRainDrop={handleRainDrop}
          playerPosition={player.position}
        />
      )}

      {/* Ambient particles */}
      <AmbientParticles
        roomSize={roomSize}
        world={currentWorld}
        roomId={currentRoom.id}
      />

      {/* Player */}
      <Player />
    </>
  );
}

// Extended particle interface for Upside Down effects
interface UpsideDownParticle extends AmbientParticle {
  baseScale: number;
  scalePhase: number;
  opacityPhase: number;
  depth: number; // 0-1, affects size and opacity for depth perception
  rotationSpeed: number;
  color: string;
}

// Ambient floating particles (fireflies, magical orbs, dust motes)
function AmbientParticles({ roomSize, world, roomId }: { roomSize: [number, number, number]; world: 'real' | 'spectral'; roomId: string }) {
  const particlesRef = useRef<AmbientParticle[]>([]);
  const upsideDownParticlesRef = useRef<UpsideDownParticle[]>([]);
  const [, forceUpdate] = useState(0);

  // Check if we're in Hawkins Upside Down
  const isHawkinsUpsideDown = roomId === 'room_hawkins_reveil' && world === 'spectral';

  // Initialize regular particles
  useMemo(() => {
    const count = 20;
    particlesRef.current = [];
    for (let i = 0; i < count; i++) {
      particlesRef.current.push({
        id: i,
        position: new THREE.Vector3(
          Math.random() * roomSize[0],
          0.5 + Math.random() * 3,
          Math.random() * roomSize[2]
        ),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.3,
          (Math.random() - 0.5) * 0.2,
          (Math.random() - 0.5) * 0.3
        ),
        scale: 0.03 + Math.random() * 0.04,
        opacity: 0.3 + Math.random() * 0.5,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }, [roomSize[0], roomSize[2]]);

  // Initialize Upside Down particles (ash/spores/petals)
  useMemo(() => {
    const count = 80; // Many more particles for atmospheric effect
    upsideDownParticlesRef.current = [];

    // Color palette for Upside Down particles (dark purples, reds, grays)
    const colors = [
      '#4a2a5a', // Dark purple
      '#3d1a4d', // Deeper purple
      '#5a2a4a', // Purple-red
      '#6a3a5a', // Lighter purple
      '#2d1f3d', // Very dark purple
      '#8b0000', // Dark red (rare)
      '#4a4a5a', // Gray-purple
      '#3a3a4a', // Dark gray
    ];

    for (let i = 0; i < count; i++) {
      const depth = Math.random(); // 0 = close (big, opaque), 1 = far (small, transparent)
      upsideDownParticlesRef.current.push({
        id: i,
        position: new THREE.Vector3(
          Math.random() * roomSize[0],
          0.2 + Math.random() * 5, // Higher range for floating ash
          Math.random() * roomSize[2]
        ),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.15, // Slower horizontal drift
          -0.05 - Math.random() * 0.1, // Gentle downward drift (falling ash)
          (Math.random() - 0.5) * 0.15
        ),
        scale: 0.02 + Math.random() * 0.06,
        baseScale: 0.02 + Math.random() * 0.06,
        opacity: 0.2 + Math.random() * 0.5,
        phase: Math.random() * Math.PI * 2,
        scalePhase: Math.random() * Math.PI * 2,
        opacityPhase: Math.random() * Math.PI * 2,
        depth,
        rotationSpeed: (Math.random() - 0.5) * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
  }, [roomSize[0], roomSize[2]]);

  useFrame((state, delta) => {
    const time = state.clock.elapsedTime;

    // Update regular particles
    particlesRef.current.forEach((particle) => {
      particle.position.x += particle.velocity.x * delta + Math.sin(time + particle.phase) * 0.002;
      particle.position.y += Math.sin(time * 0.5 + particle.phase) * 0.003;
      particle.position.z += particle.velocity.z * delta + Math.cos(time + particle.phase) * 0.002;

      if (particle.position.x < 0) particle.position.x = roomSize[0];
      if (particle.position.x > roomSize[0]) particle.position.x = 0;
      if (particle.position.z < 0) particle.position.z = roomSize[2];
      if (particle.position.z > roomSize[2]) particle.position.z = 0;
      if (particle.position.y < 0.3) particle.position.y = 0.3;
      if (particle.position.y > 4) particle.position.y = 4;

      particle.opacity = 0.3 + Math.sin(time * 2 + particle.phase) * 0.3;
    });

    // Update Upside Down particles with special animations
    if (isHawkinsUpsideDown) {
      upsideDownParticlesRef.current.forEach((particle) => {
        // Floating motion with gentle swaying
        particle.position.x += particle.velocity.x * delta + Math.sin(time * 0.3 + particle.phase) * 0.01;
        particle.position.y += particle.velocity.y * delta + Math.sin(time * 0.2 + particle.phase) * 0.005;
        particle.position.z += particle.velocity.z * delta + Math.cos(time * 0.4 + particle.phase) * 0.01;

        // Wrap around room bounds
        if (particle.position.x < -1) particle.position.x = roomSize[0] + 1;
        if (particle.position.x > roomSize[0] + 1) particle.position.x = -1;
        if (particle.position.z < -1) particle.position.z = roomSize[2] + 1;
        if (particle.position.z > roomSize[2] + 1) particle.position.z = -1;

        // Reset particles that fall too low
        if (particle.position.y < -0.5) {
          particle.position.y = 5 + Math.random() * 2;
          particle.position.x = Math.random() * roomSize[0];
          particle.position.z = Math.random() * roomSize[2];
        }

        // Animate scale for depth perception (pulsating)
        const scaleMultiplier = 1 + Math.sin(time * 0.8 + particle.scalePhase) * 0.4;
        const depthScale = 1 - particle.depth * 0.6; // Closer = bigger
        particle.scale = particle.baseScale * scaleMultiplier * depthScale;

        // Animate opacity for ethereal effect
        const opacityMultiplier = 0.6 + Math.sin(time * 0.5 + particle.opacityPhase) * 0.4;
        const depthOpacity = 0.4 + (1 - particle.depth) * 0.6; // Closer = more opaque
        particle.opacity = opacityMultiplier * depthOpacity;
      });
    }

    forceUpdate((n) => n + 1);
  });

  // Regular particle colors
  const particleColor = world === 'real' ? '#FFD700' : '#00CED1';
  const emissiveColor = world === 'real' ? '#FFA500' : '#7B68EE';

  return (
    <group>
      {/* Regular particles (hide in Hawkins Upside Down) */}
      {!isHawkinsUpsideDown && particlesRef.current.map((particle) => (
        <group key={particle.id} position={particle.position}>
          <mesh>
            <sphereGeometry args={[particle.scale * 2, 8, 8]} />
            <meshBasicMaterial
              color={particleColor}
              transparent
              opacity={particle.opacity * 0.3}
            />
          </mesh>
          <mesh>
            <sphereGeometry args={[particle.scale, 8, 8]} />
            <meshStandardMaterial
              color={particleColor}
              emissive={emissiveColor}
              emissiveIntensity={2}
              transparent
              opacity={particle.opacity}
            />
          </mesh>
        </group>
      ))}

      {/* Upside Down ash/spores/petals - Stranger Things style */}
      {isHawkinsUpsideDown && upsideDownParticlesRef.current.map((particle) => (
        <group key={`ud-${particle.id}`} position={particle.position}>
          {/* Petal/ash shape - flat disc that rotates */}
          <mesh rotation={[Math.PI / 4, particle.phase, particle.phase * 0.5]}>
            <circleGeometry args={[particle.scale, 6]} />
            <meshStandardMaterial
              color={particle.color}
              emissive={particle.color}
              emissiveIntensity={0.3}
              transparent
              opacity={particle.opacity}
              side={THREE.DoubleSide}
            />
          </mesh>
          {/* Subtle glow around particle */}
          <mesh>
            <sphereGeometry args={[particle.scale * 0.8, 6, 6]} />
            <meshBasicMaterial
              color={particle.color}
              transparent
              opacity={particle.opacity * 0.15}
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// Spectral world ambient lighting with chandeliers
function SpectralLighting({ roomSize }: { roomSize: [number, number, number] }) {
  const [width, , depth] = roomSize;

  return (
    <group>
      {/* Strong central chandelier - main light source */}
      <Chandelier position={[width / 2, 4, depth / 2]} large />

      {/* Two corner chandeliers */}
      <Chandelier position={[2, 3.5, 2]} />
      <Chandelier position={[width - 2, 3.5, depth - 2]} />

      {/* Wall sconces on opposite walls */}
      <WallSconce position={[0.5, 2.5, depth / 2]} />
      <WallSconce position={[width - 0.5, 2.5, depth / 2]} />

      {/* Strong ambient fill light for spectral world */}
      <ambientLight color="#6a7a9a" intensity={0.5} />

      {/* Additional hemisphere light for better visibility */}
      <hemisphereLight
        args={['#7a8ab0', '#2a3a5e', 0.4]}
      />
    </group>
  );
}

// Magical floating chandelier/orb
function Chandelier({ position, large = false }: { position: [number, number, number]; large?: boolean }) {
  const scale = large ? 2 : 1.2;
  const intensity = large ? 40 : 25; // Very strong single light

  return (
    <group position={position}>
      {/* Outer glow shell */}
      <mesh>
        <sphereGeometry args={[0.35 * scale, 16, 16]} />
        <meshBasicMaterial
          color="#DDA0DD"
          transparent
          opacity={0.3}
        />
      </mesh>

      {/* Main crystal orb - very bright */}
      <mesh castShadow>
        <dodecahedronGeometry args={[0.25 * scale, 0]} />
        <meshStandardMaterial
          color="#FFFFFF"
          emissive="#E6E6FA"
          emissiveIntensity={3}
          transparent
          opacity={0.95}
          roughness={0.1}
          metalness={0.2}
        />
      </mesh>

      {/* Inner bright core - pure white */}
      <mesh>
        <sphereGeometry args={[0.15 * scale, 12, 12]} />
        <meshBasicMaterial color="#FFFFFF" />
      </mesh>

      {/* Small orbiting crystals */}
      {[0, 1, 2].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos((i * Math.PI * 2) / 3) * 0.45 * scale,
            Math.sin(i * 0.5) * 0.2,
            Math.sin((i * Math.PI * 2) / 3) * 0.45 * scale,
          ]}
        >
          <octahedronGeometry args={[0.08 * scale]} />
          <meshStandardMaterial
            color="#FFFFFF"
            emissive="#87CEEB"
            emissiveIntensity={2}
          />
        </mesh>
      ))}

      {/* Single powerful light source */}
      <pointLight
        color="#E6E6FA"
        intensity={intensity}
        distance={20}
        decay={1.5}
        castShadow
      />
    </group>
  );
}

// Wall sconce light - bright magical flame
function WallSconce({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      {/* Bracket */}
      <mesh position={[0, -0.15, 0]}>
        <boxGeometry args={[0.12, 0.2, 0.12]} />
        <meshStandardMaterial color="#5a4a78" metalness={0.6} roughness={0.4} />
      </mesh>

      {/* Flame holder */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[0.08, 0.1, 0.1, 8]} />
        <meshStandardMaterial color="#6b5b95" metalness={0.5} roughness={0.5} />
      </mesh>

      {/* Bright flame/orb */}
      <mesh position={[0, 0.12, 0]}>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial
          color="#90EE90"
          emissive="#00FF7F"
          emissiveIntensity={3}
        />
      </mesh>

      {/* Inner flame core */}
      <mesh position={[0, 0.12, 0]}>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshBasicMaterial color="#FFFFFF" />
      </mesh>

      {/* Strong light */}
      <pointLight color="#90EE90" intensity={5} distance={8} decay={1.5} />
    </group>
  );
}
